import { EventCreator } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';

/**
 * The keys of `State` whose value type is exactly `Value` — the boolean loading flags or
 * the `string | null` error fields of a store state.
 *
 * Both directions of `extends` are checked (the tuples keep the check from distributing
 * over union members), so a narrower field such as `'idle' | 'loading' | 'success'` is
 * not offered as an error field just because it is assignable to `string | null`.
 */
export type KeysOfType<State, Value> = {
  [Key in keyof State]-?: [State[Key], Value] extends [Value, State[Key]] ? Key : never;
}[keyof State];

/**
 * The boolean fields of a store state that are named like a loading flag.
 *
 * The value type alone is too coarse: every boolean of the state would be offered, so a
 * typo could point the helper at an unrelated flag. Intersecting with the naming
 * convention (`isLoadingRanking`, `isSavingThing`) makes the key name part of the type,
 * which is what actually distinguishes a loading flag from any other boolean.
 */
export type LoadingKey<State> = KeysOfType<State, boolean> & `is${string}`;

/**
 * The `string | null` fields of a store state that are named like an error message.
 *
 * Same reason as {@link LoadingKey}: without the `…Error` suffix any nullable string of
 * the state compiles as the error field — `error: 'selectedGroupId'` would silently
 * overwrite the opened group's id with a failure message.
 */
export type ErrorKey<State> = KeysOfType<State, string | null> & `${string}Error`;

// `KeysOfType` has already proven that these keys hold exactly these value types, but
// TypeScript cannot narrow `State[KeysOfType<State, X>]` back to `X` on the WRITE side of
// an assignment. The two casts below re-state that proof; they are the only place in this
// file that leaves the type system's happy path.
function setLoading<State extends object>(state: State, key: LoadingKey<State>, value: boolean): void {
  (state as unknown as Record<LoadingKey<State>, boolean>)[key] = value;
}

function setError<State extends object>(state: State, key: ErrorKey<State>, value: string | null): void {
  (state as unknown as Record<ErrorKey<State>, string | null>)[key] = value;
}

export interface AsyncReducersConfig<
  State extends object,
  LoadPayload,
  SuccessPayload,
  FailedPayload extends { error: string },
> {
  /** Request started. */
  load: EventCreator<string, LoadPayload>;
  /** Request answered. */
  success: EventCreator<string, SuccessPayload>;
  /** Request rejected — its `error` goes into the {@link AsyncReducersConfig.error} field. */
  failed: EventCreator<string, FailedPayload>;
  /** The boolean field that is `true` exactly while the request is in flight. */
  loading: LoadingKey<State>;
  /**
   * The field holding the failure message — cleared on `load`, set on `failed`.
   *
   * Optional: a region whose failure is only surfaced as a snackbar (or whose message is
   * written by `onFailed` into a field shared with a neighbouring region) has no error
   * field of its own, and must not be forced to carry one nobody reads.
   */
  error?: ErrorKey<State>;
  /**
   * Guard for the two ANSWER reducers (`success` and `failed`): when it returns `false`
   * the answer is dropped completely — no flag is cleared, no error stored, no callback
   * run. For regions anchored to a currently selected entity, where a late answer for a
   * group the user has navigated away from must neither overwrite the current data nor
   * clear the flag of the request that is now in flight.
   *
   * `load` is deliberately NOT guarded: a request that is just starting is by definition
   * the current one, and it is usually `load` that MOVES the anchor (writing the new
   * selection in `onStart`), so guarding it would reject every navigation.
   */
  accepts?: (state: State, payload: SuccessPayload | FailedPayload) => boolean;
  /** Anything the region has to reset besides the loading flag and its own error. */
  onStart?: (state: State, payload: LoadPayload) => void;
  /** Writes the loaded data into the state. */
  onSuccess?: (state: State, payload: SuccessPayload) => void;
  /** Anything the region has to do beyond storing the error message. */
  onFailed?: (state: State, payload: FailedPayload) => void;
}

/**
 * Builds the three reducers every server-backed region of a store repeats: start sets the
 * loading flag and clears the previous error, success clears the flag and stores the data,
 * failure clears the flag and stores the message.
 *
 * The point is not that the call site gets shorter. It is that the invariant "start sets
 * the flag and clears the error, success AND failure clear the flag again" is written
 * down once, covered by `async-reducers.spec.ts` once, and cannot be mis-copied into the
 * next region — the failure mode of hand-written triples is a forgotten `isLoading =
 * false` or a stale error that survives a retry, and neither is visible in a diff.
 *
 * Only the bookkeeping is shared — everything region-specific stays visible at the call
 * site in `onStart` / `onSuccess` / `onFailed`, which receive the very same immer draft the
 * hand-written `immerOn` callbacks get. Regions whose lifecycle differs (a per-item saving
 * set, a status machine) keep their explicit `immerOn` blocks instead of bending this
 * helper; a response that has to be matched against the currently selected entity is
 * covered by {@link AsyncReducersConfig.accepts}.
 *
 * Spread the result into `withReducer`. The state type is supplied in the first (empty)
 * call: the key names alone cannot be inferred back to a state type, and TypeScript has no
 * partial type arguments — so the payload types stay inferred while the state type is
 * stated once:
 *
 * ```ts
 * withReducer(
 *   ...asyncReducers<RankingState>()({
 *     load: rankingEvents.loadRanking,
 *     success: rankingEvents.loadRankingSuccess,
 *     failed: rankingEvents.loadRankingFailed,
 *     loading: 'isLoadingRanking',
 *     error: 'rankingError',
 *     onSuccess: (state, payload) => {
 *       state.ranking = payload.ranking;
 *     },
 *   }),
 * )
 * ```
 */
export function asyncReducers<State extends object>() {
  return <LoadPayload, SuccessPayload, FailedPayload extends { error: string }>(
    config: AsyncReducersConfig<State, LoadPayload, SuccessPayload, FailedPayload>,
  ) => {
    const { load, success, failed, loading, error, accepts, onStart, onSuccess, onFailed } = config;

    return [
      immerOn(load, (state: State, { payload }) => {
        setLoading(state, loading, true);
        if (error) setError(state, error, null);
        onStart?.(state, payload);
      }),
      immerOn(success, (state: State, { payload }) => {
        if (accepts && !accepts(state, payload)) return;
        setLoading(state, loading, false);
        onSuccess?.(state, payload);
      }),
      immerOn(failed, (state: State, { payload }) => {
        if (accepts && !accepts(state, payload)) return;
        setLoading(state, loading, false);
        if (error) setError(state, error, payload.error);
        onFailed?.(state, payload);
      }),
    ];
  };
}
